import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import useCascada from "./useCascada";
import Filaordv, { filaConOrden } from "./Filaordv";
import {
  candidatos, esTransporte, filaVacia, llave, nombreDe, ordenPadre, padreDe, precargar, proponerOrden,
  rangoOrden, seriesDe, tieneOrdenPorDefecto, tiposDe, yaNumerado,
} from "./numeracion";
import {
  useGetListadoOrdenTiposQuery,
  useSaveOrdenTiposLoteMutation,
} from "../../libs/redux/slices/ordentipoSlice/ordentipoApiSlice";
import { setShowMessage } from "../../libs/redux/slices/confirmxxSlice/confirmxxSlice";

const SECCIONES = [
  { id: "PADRE", titulo: "Documentos padre", ayuda: "Orden de 100 en 100 (100, 200, 300...)." },
  { id: "HIJO", titulo: "Documentos hijo", ayuda: "Orden del padre + 1, + 2... (si la factura es 100, la lista de empaque es 101)." },
  { id: "OTRO", titulo: "Otros documentos", ayuda: "Numere solo los que deben ir primero (de 100 en 100); los demas salen por nombre despues." },
];
const SIN_FILAS = { PADRE: [], HIJO: [], OTRO: [] };

/** El nombre de la fila del tracking se muestra aunque el combo aun cargue */
const opcion = (lista, valor, texto) =>
  lista.some((o) => String(o.value) === valor) || !valor || !texto ? lista : [{ value: valor, label: texto }, ...lista];

/**
 * Crear / editar / ver el orden de visualizacion de un proceso (GEST0025).
 * Cabecera con empresa y proceso y tres secciones (documentos padre, documentos hijo y otros
 * documentos) con todos los tipos del proceso en filas de serie, tipo y orden. Al crear se
 * eligen empresa y proceso; al editar o ver vienen fijos del tracking y Ver es solo lectura.
 * En Agencia de Aduanas SIACO / Importaciones, si el proceso aun no tiene la numeracion, los
 * padres e hijos se proponen con el orden actual (lista de chequeo de CIMA). Se guardan juntas,
 * en un lote, las filas cuyo orden cambio; borrar el orden de un tipo se lo quita.
 * @param {function} onClose    cierra el modal
 * @param {object}   row        fila del tracking (EMPNITXX, EMPDESXX, AREAIDXX, AREADESX)
 * @param {boolean}  isCreating
 * @param {boolean}  isEdit     true = solo lectura (Ver)
 */
const Creordvx = ({ onClose, row, isCreating, isEdit }) => {
  const dispatch = useDispatch();
  const cabeceraFija = !isCreating;
  const soloLectura = isEdit;
  const [validated, setValidated] = useState(false);
  const [error, setError] = useState("");
  const [empresa, setEmpresa] = useState(row?.EMPNITXX || "");
  const [proceso, setProceso] = useState(row?.AREAIDXX ? String(row.AREAIDXX) : "");
  const [filas, setFilas] = useState(SIN_FILAS);
  const [precargadoPara, setPrecargadoPara] = useState("");
  //Secciones desplegadas; Otros documentos (la mas larga) inicia recogida
  const [abiertas, setAbiertas] = useState({ PADRE: true, HIJO: true, OTRO: false });
  const [buscar, setBuscar] = useState({});
  const { empresas, procesos } = useCascada(empresa, "", "");
  const { currentData, isFetching } = useGetListadoOrdenTiposQuery(
    { EMPNITXX: empresa, AREAIDXX: proceso },
    { skip: !empresa || !proceso }
  );
  const [saveLote, { isLoading: isSaving }] = useSaveOrdenTiposLoteMutation();

  const listado = currentData?.data || [];
  const llaveProceso = `${empresa}|${proceso}`;
  const ordenPorDefecto = isCreating && tieneOrdenPorDefecto(empresa, proceso) && !yaNumerado(listado);

  //Cada vez que cambian empresa y proceso se cargan todos sus tipos por seccion
  useEffect(() => {
    if (!currentData || precargadoPara === llaveProceso) return;
    const datos = currentData.data || [];
    setPrecargadoPara(llaveProceso);
    setFilas(precargar(datos, isCreating && tieneOrdenPorDefecto(empresa, proceso) && !yaNumerado(datos)));
  }, [currentData, precargadoPara, llaveProceso, empresa, proceso, isCreating]);

  const cambiarCabecera = (setter) => (v) => {
    setter(v);
    setFilas(SIN_FILAS);
    setPrecargadoPara("");
  };

  //Al cambiar un campo se limpian los que dependen de el y, al elegir el tipo, se propone el orden
  const cambiarFila = (seccion, id, campo, valor) =>
    setFilas((prev) => {
      const fila = prev[seccion].find((f) => f.id === id);
      const limpios = { serie: { terna: "", orden: "", padre: "" }, terna: {}, orden: {} }[campo];
      let nueva = { ...fila, ...limpios, [campo]: valor };
      if (campo === "terna" && valor) {
        nueva = { ...nueva, padre: seccion === "HIJO" ? padreDe(valor, listado) : "" };
        nueva = { ...nueva, orden: proponerOrden(seccion, nueva, listado, prev) };
      }
      return { ...prev, [seccion]: prev[seccion].map((f) => (f.id === id ? nueva : f)) };
    });
  const agregarFila = (seccion, id) =>
    setFilas((prev) => {
      const i = prev[seccion].findIndex((f) => f.id === id);
      return { ...prev, [seccion]: [...prev[seccion].slice(0, i + 1), filaVacia(), ...prev[seccion].slice(i + 1)] };
    });
  const quitarFila = (seccion, id) => setFilas((prev) => ({ ...prev, [seccion]: prev[seccion].filter((f) => f.id !== id) }));

  const listasDe = (seccion, fila) => {
    const cands = candidatos(listado, seccion);
    return { series: seriesDe(cands), tipos: tiposDe(cands, fila.serie) };
  };
  //Los hijos del transporte cuelgan de AWB, BL y carta porte a la vez: se nombra la serie
  const padreTexto = (fila) => {
    if (!fila.padre) return "";
    const orden = ordenPadre(fila.padre, listado, filas);
    const padre = listado.find((f) => llave(f) === fila.padre);
    const nombre = padre && esTransporte(padre) ? padre.SERIEDES : nombreDe(fila.padre, listado);
    return `${nombre} (${orden !== null ? `orden ${orden}` : "sin orden"})`;
  };

  //Solo viajan los tipos cuyo orden cambio frente a lo guardado; borrar el orden lo quita (null)
  const cambiadas = () =>
    SECCIONES.flatMap((s) => filas[s.id].filter((f) => f.terna)).filter(
      (f) => (filaConOrden(f) ? Number(f.orden) : null) !== (listado.find((l) => llave(l) === f.terna)?.ORDENXXX ?? null)
    );

  const guardar = async (lote) => {
    const ORDENESX = lote.map((f) => {
      const [EMPNITXX, AREAIDXX, SERIEIDX, TIPOIDXX] = f.terna.split("|");
      const ORDENXXX = filaConOrden(f) ? Number(f.orden) : null;
      return { EMPNITXX, AREAIDXX: Number(AREAIDXX), SERIEIDX: Number(SERIEIDX), TIPOIDXX: Number(TIPOIDXX), ORDENXXX };
    });
    await saveLote({ ORDENESX })
      .unwrap()
      .then(() => {
        const quitados = ORDENESX.filter((f) => f.ORDENXXX === null).length;
        const guardados = ORDENESX.length - quitados;
        const partes = [guardados > 0 && `${guardados} ${guardados === 1 ? "orden guardado" : "ordenes guardados"}`,
          quitados > 0 && `${quitados} ${quitados === 1 ? "orden quitado" : "ordenes quitados"}`].filter(Boolean);
        const mensaje = `${partes.join(" y ")} correctamente.`;
        dispatch(setShowMessage({ message: mensaje }));
        onClose(false);
      })
      .catch((e) => setError(e?.data?.errors?.ORDENESX?.join(" ") || "No se pudo guardar; no se guardo ninguna fila."));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    if (!e.currentTarget.checkValidity()) {
      //Las secciones recogidas tambien se validan: se despliegan las que tienen errores
      const conError = [...e.currentTarget.querySelectorAll(":invalid")].map((el) => el.closest("[data-seccion]")?.dataset.seccion);
      setAbiertas((prev) => conError.reduce((acc, id) => (id ? { ...acc, [id]: true } : acc), prev));
      setValidated(true);
      return;
    }
    const conOrden = SECCIONES.flatMap((s) => filas[s.id].filter(filaConOrden));
    const ternas = conOrden.map((f) => f.terna);
    if (new Set(ternas).size !== ternas.length) {
      setError("Hay un tipo documental repetido en dos filas: cada tipo lleva un solo orden.");
      return;
    }
    const lote = cambiadas();
    if (lote.length === 0) {
      setError("No hay cambios para guardar.");
      return;
    }
    guardar(lote);
  };

  const cabecera = (label, valor, onChange, lista, deshabilitado) => (
    <Form.Group as={Col} md={6}>
      <Form.Label>{label}*</Form.Label>
      <Form.Select value={valor} onChange={(e) => onChange(e.target.value)} required disabled={deshabilitado}>
        <option value="">[SELECCIONE]</option>
        {lista.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </Form.Select>
      <Form.Control.Feedback type="invalid">Debe Seleccionar {label === "Empresa" ? "una Empresa" : "un Proceso"}</Form.Control.Feedback>
    </Form.Group>
  );

  //Buscador de la seccion: por tipo documental o serie (las filas nuevas sin tipo siempre se ven)
  const visible = (seccionId, fila) => {
    const texto = (buscar[seccionId] || "").trim().toUpperCase();
    if (!texto || !fila.terna) return true;
    const tipo = listado.find((l) => llave(l) === fila.terna);
    return `${tipo?.TIPODESX || ""} ${tipo?.SERIEDES || ""}`.toUpperCase().includes(texto);
  };

  const seccion = (s) => {
    const abierta = abiertas[s.id];
    const sinResultados = filas[s.id].length > 0 && !filas[s.id].some((f) => visible(s.id, f));
    return (
      <Card key={s.id} className="mt-3" data-seccion={s.id}>
        <Card.Header
          className="text-white d-flex align-items-center"
          style={{ backgroundColor: "#fba500", cursor: "pointer" }}
          role="button"
          aria-expanded={abierta}
          onClick={() => setAbiertas((prev) => ({ ...prev, [s.id]: !prev[s.id] }))}
        >
          <i className={`bi ${abierta ? "bi-chevron-down" : "bi-chevron-right"} me-3`} />
          <div className="flex-grow-1">
            <b>{s.titulo}</b>
            <div className="small">{s.ayuda}</div>
          </div>
        </Card.Header>
        <Card.Body className={abierta ? "pt-2" : "d-none"}>
          {filas[s.id].length === 0 && <div className="small text-muted mt-2">El proceso no tiene tipos documentales en esta seccion.</div>}
          {filas[s.id].length > 1 && (
            <Form.Control
              type="search"
              size="sm"
              placeholder="Buscar tipo documental o serie..."
              value={buscar[s.id] || ""}
              onChange={(e) => setBuscar((prev) => ({ ...prev, [s.id]: e.target.value }))}
            />
          )}
          {sinResultados && <div className="small text-muted mt-2">Ningun tipo coincide con la busqueda.</div>}
          {filas[s.id].map((f) => (
            <Filaordv
              key={f.id}
              oculta={!visible(s.id, f)}
              sinAsterisco={s.id === "OTRO"}
              fila={f}
              listas={listasDe(s.id, f)}
              rango={rangoOrden(s.id, f, listado, filas)}
              padre={s.id === "HIJO" ? padreTexto(f) : ""}
              onChange={(campo, valor) => cambiarFila(s.id, f.id, campo, valor)}
              soloLectura={soloLectura}
              acciones={soloLectura ? null : {
                onAgregar: () => agregarFila(s.id, f.id),
                onQuitar: () => quitarFila(s.id, f.id),
                puedeQuitar: filas[s.id].length > 1,
              }}
            />
          ))}
        </Card.Body>
      </Card>
    );
  };

  const cuerpo = () => {
    if (!empresa || !proceso) return <div className="small text-muted mt-3">Seleccione la empresa y el proceso.</div>;
    if (isFetching || precargadoPara !== llaveProceso) {
      return <Spinner animation="border" role="status" className="mt-3"><span className="visually-hidden">Loading...</span></Spinner>;
    }
    return (
      <>
        {!soloLectura && (
          <div className="small text-muted mt-3">
            {ordenPorDefecto
              ? "Se cargaron todos los tipos del proceso: padres e hijos con el orden actual (lista de chequeo de CIMA). Revise y guarde."
              : "Se cargaron todos los tipos del proceso. Digite el orden de los que quiera ordenar: las filas sin orden no se guardan."}
            {" "}Si borra el orden de un tipo que ya lo tenia, al guardar se le quita y vuelve al orden por defecto.
          </div>
        )}
        {SECCIONES.map(seccion)}
      </>
    );
  };

  if (isSaving) {
    return (
      <Spinner animation="border" role="status">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );
  }

  return (
    <Form noValidate validated={validated} onSubmit={handleSubmit}>
      <Row className="mt-2 pb-3 border-bottom">
        {cabecera("Empresa", empresa, cambiarCabecera((v) => { setEmpresa(v); setProceso(""); }),
          opcion(empresas, empresa, row?.EMPDESXX), cabeceraFija)}
        {cabecera("Proceso", proceso, cambiarCabecera(setProceso),
          opcion(procesos, proceso, row?.AREADESX), cabeceraFija || !empresa)}
      </Row>
      {cuerpo()}
      {error && <div className="text-danger mt-3">{error}</div>}
      <Row className="mt-4 justify-content-end">
        {!soloLectura && (
          <Col md="auto">
            <Button type="submit">Guardar</Button>
          </Col>
        )}
        {!soloLectura && (
          <Col md="auto">
            <Button
              onClick={() => {
                setValidated(false);
                setError("");
                setPrecargadoPara("");
              }}
            >
              Limpiar
            </Button>
          </Col>
        )}
      </Row>
    </Form>
  );
};

export default Creordvx;
